import { db } from "@stores/environments/Database.web";
import { ref, push, set } from "firebase/database";
import { ILog } from "@interfaces/ilog";
import { uuidv4 } from "../../../domain/utils/idUtils";
import { authStore } from "../stores/AuthStore";

export class LogService {
  private static instance: LogService;

  private constructor() {}

  public static getInstance(): LogService {
    if (!LogService.instance) {
      LogService.instance = new LogService();
    }
    return LogService.instance;
  }

  /**
   * Registra um log de operação no Firebase Database
   * @param logData - Dados do log a serem registrados
   */
  public async logOperation(
    logData: Omit<ILog, 'id' | 'timestamp' | 'userId' | 'userEmail'>
  ): Promise<void> {
    try {
      // Verifica se há usuário logado
      if (!authStore._user) {
        console.warn('Tentativa de log sem usuário autenticado');
        return;
      }

      const now = new Date();
      const day = now.getDate().toString().padStart(2, '0');
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const year = now.getFullYear().toString();
      const dateStr = `${day}-${month}-${year}`;
      const timestamp = now.toISOString();
      
      // Gera um UUID para o log
      const logId = uuidv4();
      
      const log: ILog = {
        id: logId,
        timestamp,
        userId: authStore._user.uid,
        userEmail: authStore._user.email || 'unknown@email.com',
        ...logData
      };

      // Caminho do log: /rules/logs/DD-MM-YYYY/userUID/UUID
      const logPath = `rules/logs/${dateStr}/${authStore._user.uid}/${logId}`;
      const logRef = ref(db, logPath);
      
      await set(logRef, log);
      
      console.log(`Log registrado: ${logData.action} em ${logData.entity}`, log);
    } catch (error) {
      console.error('Erro ao registrar log:', error);
      // Não lança erro para não interromper a operação principal
    }
  }


  /**
   * Helper para criar dados de log para operações CREATE
   */
  public createLogData(
    entity: string,
    entityId: string,
    entityPath: string,
    newValue: any,
    details?: string
  ): Omit<ILog, 'id' | 'timestamp' | 'userId' | 'userEmail'> {
    return {
      action: 'CREATE',
      entity,
      entityId,
      entityPath,
      newValue,
      details
    };
  }

  /**
   * Helper para criar dados de log para operações UPDATE
   */
  public updateLogData(
    entity: string,
    entityId: string,
    entityPath: string,
    oldValue: any,
    newValue: any,
    details?: string
  ): Omit<ILog, 'id' | 'timestamp' | 'userId' | 'userEmail'> {
    return {
      action: 'UPDATE',
      entity,
      entityId,
      entityPath,
      oldValue,
      newValue,
      details
    };
  }

  /**
   * Helper para criar dados de log para operações DELETE
   */
  public deleteLogData(
    entity: string,
    entityId: string,
    entityPath: string,
    oldValue: any,
    details?: string
  ): Omit<ILog, 'id' | 'timestamp' | 'userId' | 'userEmail'> {
    return {
      action: 'DELETE',
      entity,
      entityId,
      entityPath,
      oldValue,
      details
    };
  }

  /**
   * Helper para criar dados de log para operações TOGGLE
   */
  public toggleLogData(
    entity: string,
    entityId: string,
    entityPath: string,
    oldValue: any,
    newValue: any,
    details?: string
  ): Omit<ILog, 'id' | 'timestamp' | 'userId' | 'userEmail'> {
    return {
      action: 'TOGGLE',
      entity,
      entityId,
      entityPath,
      oldValue,
      newValue,
      details
    };
  }
}

export const logService = LogService.getInstance();
